import { deltaTone, fmtDelta, fmtDuration, fmtTime } from "../components/geotab/geotabFormat.js";

// Avis Geotab en direct : arrivées et fins d'arrêt de la journée en cours, chez les clients, aux meuneries et hors plan.
// Temps qu'une carte reste à l'écran (secondes) ; 0 : jusqu'à ce qu'on la ferme.
export const ALERT_DURATIONS = [[10, "10 s"], [20, "20 s"], [30, "30 s"], [60, "1 min"], [0, "Fixe"]];
export const ALERT_POLL_MS = 60_000;
// Un avis plus vieux (page rouverte, Geotab en retard) va dans la cloche sans carte.
export const ALERT_FRESH_MIN = 15;
export const MAX_ALERT_CARDS = 2;

// Lieux des avis : option du menu (réglage par défaut), libellé, précision et couleur de pastille.
export const ALERT_PLACES = [
  { kind: "delivery", option: "alert_deliveries", label: "Clients", hint: "Livraisons", color: "#d3c195" },
  { kind: "loading", option: "alert_loadings", label: "Meuneries", hint: "Chargements", color: "#3f5585" },
  { kind: "off", option: "alert_offplan", label: "Hors plan", hint: "Arrêt de 10 min et plus", color: "#c2410c" },
];
export const ALERT_TYPES = [["arrival", "Arrivée"], ["departure", "Terminé"]];
export const ALL_ALERT_KINDS = ALERT_PLACES.flatMap((place) => ALERT_TYPES.map(([type]) => `${place.kind}:${type}`));

// Arrivée et Terminé du menu coupent ces avis pour tous les chauffeurs.
export const alertsEnabled = (display) => Boolean(display?.alert_arrival || display?.alert_departure);

const ruleOf = (display, key) => (display?.alert_driver_rules || []).find((rule) => rule.key === key) || null;

// Avis d'un chauffeur : son réglage à part, sinon ceux du menu (ou aucun avec « Aucun avis pour les autres »).
export function driverAlertKinds(display, key) {
  const rule = ruleOf(display, key);
  if (rule) return rule.kinds;
  if (display?.alert_others_off) return [];
  return ALERT_PLACES.filter((place) => display?.[place.option]).flatMap((place) => ALERT_TYPES.map(([type]) => `${place.kind}:${type}`));
}

// « default » : avis du menu ; « custom » : réglé à part ; « none » : aucun avis.
export function driverAlertStatus(display, key) {
  const rule = ruleOf(display, key);
  if (rule) return rule.kinds.length ? "custom" : "none";
  return display?.alert_others_off ? "none" : "default";
}

export function alertVisible(event, display) {
  if (!(event.type === "arrival" ? display.alert_arrival : display.alert_departure)) return false;
  return driverAlertKinds(display, event.driver_key).includes(`${event.site_kind}:${event.type}`);
}

// Résumé des avis d'un chauffeur réglé à part : les lieux où il en reçoit.
export function alertKindsSummary(kinds) {
  if (!kinds.length) return "Aucun avis";
  if (ALL_ALERT_KINDS.every((kind) => kinds.includes(kind))) return "Tous les avis";
  return ALERT_PLACES.filter((place) => kinds.some((kind) => kind.startsWith(`${place.kind}:`))).map((place) => place.label).join(" · ");
}

// Réglage à part d'un chauffeur ; kinds null : retour aux avis du menu.
export function withDriverRule(display, driver, kinds) {
  const rules = (display.alert_driver_rules || []).filter((rule) => rule.key !== driver.driver_key);
  return kinds == null ? rules : [...rules, { key: driver.driver_key, name: driver.driver, kinds }];
}

// Tête du menu : les chauffeurs du jour qui reçoivent des avis, ceux qui n'en reçoivent aucun et ceux réglés à part.
export function alertCoverage(display, drivers) {
  const today = drivers || [];
  const none = today.filter((driver) => driverAlertStatus(display, driver.driver_key) === "none");
  const custom = today.filter((driver) => driverAlertStatus(display, driver.driver_key) === "custom")
    .map((driver) => ({ ...driver, summary: alertKindsSummary(driverAlertKinds(display, driver.driver_key)) }));
  return { total: today.length, withAlerts: today.length - none.length, none, custom, limited: Boolean(none.length || custom.length) };
}

// Écart du temps sur place d'une fin d'arrêt par rapport au Dispatch, toujours montré ; vert dans la tolérance.
export function dwellGap(event, tolerance) {
  if (event.type !== "departure" || event.dwell == null || event.plan_dwell == null) return null;
  const gap = event.dwell - event.plan_dwell;
  return { gap, tone: deltaTone(gap, tolerance) };
}

// Pastille : orange brûlé hors plan ; vert pour un arrêt terminé ; à l'arrivée, bleu au chargement (meunerie), sable chez le
// client, comme dans le Gantt.
export const alertTone = (event) => (event.site_kind === "off" ? "off" : event.type === "departure" ? "done"
  : event.site_kind === "loading" ? "loading" : "delivery");

export const placeLabel = (event) => (event.site_kind === "loading" ? "Chargement" : event.site_kind === "off" ? "Hors plan" : "Livraison");
export const alertAction = (event) => (event.site_kind === "off"
  ? (event.type === "arrival" ? "Arrêt hors plan à" : "Reparti de")
  : (event.type === "arrival" ? "Arrivée chez" : "Terminé chez"));

// Avis Windows : texte simple, le chauffeur d'abord.
export function alertNotificationText(event) {
  const off = event.site_kind === "off";
  const detail = event.type === "arrival"
    ? (off ? `Arrêté depuis ${fmtDuration(event.elapsed)} · pas dans le Dispatch`
      : `${placeLabel(event)}${event.plan_dwell != null ? ` · prévu ${fmtDuration(event.plan_dwell)} sur place` : ""}`)
    : `${fmtDuration(event.dwell)} ${off ? "hors plan" : "sur place"}${event.plan_dwell != null ? `, écart ${fmtDelta(event.dwell - event.plan_dwell)}` : ""}${event.next ? ` · suivant : ${event.next}` : ""}`;
  return {
    title: [event.driver, event.truck].filter(Boolean).join(" · "),
    body: `${alertAction(event)} ${event.site} · ${fmtTime(event.at)}\n${detail}`,
  };
}

// Nouveaux avis d'une lecture : chacun n'est vu qu'une fois. Au premier passage de la journée, ce qui s'est déjà passé
// est classé lu, sans carte. Ensuite, les avis récents donnent une carte ; les autres restent non lus dans la cloche.
// Un arrêt hors plan n'est annoncé qu'après 10 min : sa fraîcheur part de là (notice_at).
export function takeNewAlerts(events, { seen, read, display, now, firstLoad }) {
  const cards = [];
  for (const event of events) {
    if (seen.has(event.id)) continue;
    seen.add(event.id);
    if (firstLoad || !alertVisible(event, display)) read.add(event.id);
    else if (now - (event.notice_at ?? event.at) <= ALERT_FRESH_MIN) cards.push(event);
  }
  return cards;
}

// Cloche : avis groupés par heure, du plus récent au plus ancien.
export function groupAlertsByHour(events) {
  const groups = [];
  for (const event of events) {
    const hour = Math.floor(Math.round(event.at) / 60) % 24;
    if (groups.at(-1)?.hour !== hour) groups.push({ hour, events: [] });
    groups.at(-1).events.push(event);
  }
  return groups;
}

// Liste des chauffeurs : ceux du Dispatch du jour, plus ceux réglés à part sans tournée aujourd'hui.
export function alertDriverChoices(drivers, rules) {
  const today = new Map((drivers || []).map((driver) => [driver.driver_key, driver]));
  const absent = (rules || []).filter((rule) => !today.has(rule.key))
    .map((rule) => ({ driver: rule.name || rule.key, driver_key: rule.key, truck: "", state: "", absent: true }));
  return [...today.values(), ...absent].sort((a, b) => a.driver.localeCompare(b.driver, "fr"));
}

// Ligne du Gantt : même clé que le serveur (employé, sinon nom).
export function assignmentAlertStatus(display, assignment) {
  if (!display || !assignment || !(assignment.driver_employee_id || String(assignment.driver_name || "").trim())) return "default";
  const keys = [assignment.driver_employee_id ? `e:${assignment.driver_employee_id}` : "", `n:${String(assignment.driver_name || "").trim().toLowerCase()}`];
  const key = keys.find((candidate) => ruleOf(display, candidate)) || keys.find(Boolean);
  return driverAlertStatus(display, key);
}
